import { Suspense, lazy, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, ApiError, NOT_MODIFIED, type FuelUnit, type MissionDetail as Detail, type MissionEvent } from "../lib/api";
import { ArrowLeft, Download, Droplet, Fan, Flame, Fuel, Plane } from "lucide-react";
import { useAuth } from "../lib/auth";
import { Alert, PageTitle, Panel, Pill, StatTile } from "../components/ui";
import { EventBadge } from "../lib/eventStyle";
import { usePolling } from "../lib/usePolling";
import { SPEED_RAMP, buildRoute, type Route } from "../lib/route";
import {
  NO_ROUNDING, describeRules, formatHours, hoursDecimals, isRounding, roundMinutes, wholeMinutes,
  type RoundingRule,
} from "../lib/rounding";

// MapLibre is most of a megabyte; only a mission page with positions pays for it.
const MissionMap = lazy(() => import("../components/MissionMap"));
import {
  EVENT_LABEL, amount, averageBurn, dayAndMonth, decimalHours,
  splitIntoLegs, timeOfDay,
} from "../lib/missionFormat";

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-3 border-b border-rule-soft last:border-b-0">
      <span className="text-[1rem] text-ink">{label}</span>
      <span className="font-mono text-[0.95rem] text-ink-soft text-right">{value}</span>
    </div>
  );
}

/** One line of the event log — time, what happened, and anything it recorded. */
function EventRow({ event, from, display }: { event: MissionEvent; from: FuelUnit; display: FuelUnit }) {
  const detail: string[] = [];
  if (event.refuelAmount) {
    detail.push(amount(event.refuelAmount, from, display) + (event.refuelSupplier ? ` · ${event.refuelSupplier}` : ""));
  }
  if (event.fuelLevel) {
    detail.push(`gauge ${amount(event.fuelLevel, from, display)}`);
  }
  if (event.suburb) detail.push(event.suburb);

  return (
    <li className="flex items-center gap-4 py-2.5 border-t border-rule-soft first:border-t-0">
      <span className="font-mono text-[0.95rem] text-ink-soft tabular-nums w-14 shrink-0">
        {timeOfDay(event.recordedAt)}
      </span>
      <EventBadge event={event.event} />
      <span className="text-[1rem] font-semibold text-ink w-40 shrink-0">
        {EVENT_LABEL[event.event] ?? event.event}
      </span>
      <span className="text-[0.95rem] text-ink-soft">{detail.join(" · ")}</span>
    </li>
  );
}

export function MissionDetail() {
  const { id } = useParams<{ id: string }>();
  const auth = useAuth();
  const [mission, setMission] = useState<Detail | null>(null);
  const [events, setEvents] = useState<MissionEvent[]>([]);
  // The rule this mission reports under. Missing means exact, which is also
  // what any mission from before ODRs existed does.
  const [rules, setRules] = useState<{ engine: RoundingRule; flight: RoundingRule }>({
    engine: NO_ROUNDING, flight: NO_ROUNDING,
  });
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    if (!id) return;
    try {
      const res = await api.getMission(id);
      if (res === NOT_MODIFIED) return;
      setMission(res.mission);
      setEvents(res.events);
      setRules(res.report ? { engine: res.report.engineTime, flight: res.report.flightTime } : { engine: NO_ROUNDING, flight: NO_ROUNDING });
      setError(null);
    } catch (err) {
      if (mission === null) {
        setError(err instanceof ApiError ? err.message : "Couldn't load this mission.");
      }
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // A mission still being flown is the only thing here that changes minute to
  // minute; a finished one only moves if a pilot reopens and edits it.
  usePolling(load, mission?.endedAt === null ? 15_000 : 60_000);

  const route = useMemo(() => buildRoute(events), [events]);

  if (error) return <Alert>{error}</Alert>;
  if (!mission) return <p className="text-[0.95rem] text-ink-soft">Loading…</p>;

  const legs = splitIntoLegs(events);
  // A leg's hours as the sheet prints them: rounded only when the rule is per
  // leg, since "on the total" leaves each leg as flown.
  const legHours = (seconds: number) =>
    isRounding(rules.flight) && rules.flight.scope === "perLeg"
      ? formatHours(roundMinutes(wholeMinutes(seconds), rules.flight), hoursDecimals(rules.flight))
      : decimalHours(seconds);
  const roundingNotes = describeRules(rules.engine, rules.flight);
  // The organisation's chosen unit, as the export uses — so the screen and the
  // PDF it produces never disagree. Unset means each mission's own unit.
  const display: FuelUnit =
    (auth.status === "authenticated" ? auth.me.organization.reportFuelUnit : null) ?? mission.fuelUnit;
  const from = mission.fuelUnit;
  const firstPlace = events.find((e) => e.suburb)?.suburb;
  const lastPlace = [...events].reverse().find((e) => e.suburb)?.suburb;

  return (
    <div className="flex flex-col gap-8">
      <div className="flex items-end justify-between gap-6 flex-wrap">
        <div>
        <Link
          to="/app/missions"
          className="inline-flex items-center gap-1 text-[1rem] text-accent-text hover:underline underline-offset-4 mb-3"
        >
          <ArrowLeft size={18} strokeWidth={2.4} aria-hidden />
          Missions
        </Link>
        <PageTitle
          detail={
            <>
              {dayAndMonth(mission.startedAt)} · {timeOfDay(mission.startedAt)}
              {mission.endedAt ? ` – ${timeOfDay(mission.endedAt)}` : " "}
              {mission.endedAt === null && <Pill className="bg-good/15 text-good ml-1">In progress</Pill>}
            </>
          }
        >
          {mission.missionName}
        </PageTitle>
        </div>

        {/* A plain link rather than fetch-and-blob: the session cookie rides
            along, the browser handles the download, and it works the same in
            every browser. */}
        <a
          href={api.sheetUrl(mission.id)}
          download
          className="h-11 px-5 inline-flex items-center gap-2 rounded-lg font-semibold text-[0.95rem] bg-accent text-on-accent hover:brightness-105 active:brightness-95"
        >
          <Download size={19} strokeWidth={2.5} aria-hidden />
          Export PDF
        </a>
      </div>

      {/* The app's summary tiles, with the same icons it uses for each figure. */}
      <div>
      <Panel className="grid grid-cols-2 sm:grid-cols-3 px-2 py-2">
        <StatTile
          icon={Fan}
          label="Engine time"
          value={`${formatHours(mission.reportedEngineMinutes, mission.reportedEngineDp)} h`}
        />
        <StatTile
          icon={Plane}
          label="Flight time"
          value={`${formatHours(mission.reportedAirMinutes, mission.reportedAirDp)} h`}
        />
        <StatTile icon={Droplet} label="Drops" value={String(mission.dropCount)} />
        <StatTile icon={Fan} label="Engine starts" value={String(mission.engineStartCount)} />
        <StatTile icon={Fuel} label="Fuel used" value={amount(mission.fuelBurnt, from, display)} />
        <StatTile icon={Flame} label="Average burn" value={averageBurn(mission, display)} />
      </Panel>
      {roundingNotes.length > 0 && (
        <p className="mt-2.5 px-1 text-[0.9rem] text-ink-soft leading-relaxed">
          {roundingNotes.join(" ")} Burn uses the exact time.
        </p>
      )}
      </div>

      {route.points.length > 0 && (
        <div>
          <div className="flex items-baseline justify-between gap-4 mb-2.5 px-1 flex-wrap">
            <p className="section-label">Route</p>
            <SpeedLegend range={route.range} />
          </div>
          <Panel className="p-1.5">
            <Suspense fallback={<div className="h-80 sm:h-[440px]" />}>
              <MissionMap route={route} live={mission.endedAt === null} />
            </Suspense>
          </Panel>
        </div>
      )}

      <div>
        <p className="section-label mb-2.5 px-1">Task &amp; aircraft</p>
        <Panel className="px-5 py-1">
          <Row
            label="Aircraft"
            value={[mission.aircraftRegistration, mission.aircraftCallsign].filter(Boolean).join(" · ") || "—"}
          />
          {mission.task && <Row label="Task" value={mission.task} />}
          {mission.companyName && <Row label="Company" value={mission.companyName} />}
          {mission.pilotName && <Row label="Pilot" value={mission.pilotName} />}
          {firstPlace && (
            <Row
              label="Location"
              value={lastPlace && lastPlace !== firstPlace ? `${firstPlace} → ${lastPlace}` : firstPlace}
            />
          )}
          <Row
            label="Fuel"
            value={
              `${amount(mission.startingFuel, from, display)} start` +
              ` · ${amount(mission.fuelUplifted, from, display)} uplifted` +
              ` · ${amount(mission.endFuel, from, display)} end`
            }
          />
        </Panel>
      </div>

      <div>
        <p className="section-label mb-2.5 px-1">
          Event log · {legs.length} {legs.length === 1 ? "leg" : "legs"}
        </p>

        <div className="flex flex-col gap-4">
          {legs.map((leg) => (
            <Panel key={leg.index} className="px-5 py-3">
              <div className="flex items-baseline justify-between gap-4 mb-2 flex-wrap">
                <p className="text-[1.05rem] font-bold text-ink">Leg {leg.index}</p>
                <p className="font-mono text-[0.9rem] text-ink-soft tabular-nums">
                  {leg.airborneSeconds !== null
                    ? `${legHours(leg.airborneSeconds)} h`
                    : leg.events.some((e) => e.event === "takeoff")
                      ? "airborne"
                      : "not flown"}
                  {leg.drops > 0 && ` · ${leg.drops} drops`}
                </p>
              </div>
              <ul>
                {leg.events.map((e) => (
                  <EventRow key={e.id} event={e} from={from} display={display} />
                ))}
              </ul>
            </Panel>
          ))}
        </div>
      </div>
    </div>
  );
}

/** Positions exist only where a button was pressed, so speeds are averages. */
function SpeedLegend({ range }: { range: Route["range"] }) {
  if (!range) return null;
  const same = Math.round(range.min) === Math.round(range.max);
  return (
    <div className="flex items-center gap-2 text-[0.85rem] text-ink-soft tabular-nums">
      <span>Average speed between events</span>
      {same ? (
        <span className="font-semibold text-ink">{Math.round(range.min)} kn</span>
      ) : (
        <>
          <span className="font-semibold text-ink">{Math.round(range.min)}</span>
          <span
            className="h-2 w-20 rounded-full"
            style={{ background: `linear-gradient(to right, ${SPEED_RAMP.join(", ")})` }}
            aria-hidden
          />
          <span className="font-semibold text-ink">{Math.round(range.max)} kn</span>
        </>
      )}
    </div>
  );
}
